import React from "react";
import { useTranslation } from "react-i18next";
import { useSnapshot } from "valtio";
import { MdCloudUpload } from "react-icons/md";
import { Card, Input } from "reactstrap";
import classnames from "classnames";
import { TrashIcon } from "@aureskonnect/react-ui";

import {
    storeDivers,
    setQrCodeType,
    setQrCodeUrl,
    setQrCodeImage,
} from "@components/Common/PrinterSetting/stores";
import { useFetchGlobal } from "../../../../../api/hooks";
import { setIsLoading } from "../../../../../store/project";
import {
    createHandleFileChange,
    createHandleDrop,
    createHandleDeleteImage,
} from "./helpers";
import "../index.css";

export default function ConfigQRCode() {
    const { t } = useTranslation();
    const { qrCodeType, qrCodeUrl, qrCodeImage } = useSnapshot(storeDivers);
    const { fetchGlobal } = useFetchGlobal();
    const [dragging, setDragging] = React.useState(false);
    const [displayImage, setDisplayImage] = React.useState("");

    const handleFileChange = createHandleFileChange({
        t,
        setDisplayImage,
        setQrCodeImage,
        setIsLoading,
        fetchGlobal,
    });

    const handleDropCore = createHandleDrop({
        t,
        setDisplayImage,
        setQrCodeImage,
        setIsLoading,
        fetchGlobal,
    });

    const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.stopPropagation();
        setDragging(false);
        await handleDropCore(e);
    };

    const handleDeleteImage = createHandleDeleteImage(setQrCodeImage);

    return (
        <div className="ml-3 mt-2">
            <div className="py-1">
                <span className="qr-config-subtitle__clz">
                    {t("QR Code Configuration")}
                </span>
            </div>

            <div className="pl-2">
                <div className="d-flex align-items-center mb-3">
                    <input
                        type="radio"
                        id="qr-type-url"
                        name="qrCodeType"
                        checked={qrCodeType === "url"}
                        onChange={() => {
                            setQrCodeType("url");
                            handleDeleteImage();
                        }}
                        className="mr-2"
                    />
                    <label htmlFor="qr-type-url" className="mb-0 mr-3">
                        {t("Print QR code order number")} + URL / Text
                    </label>
                    <Input
                        type="text"
                        value={qrCodeUrl}
                        onChange={(e) => setQrCodeUrl(e.target.value)}
                        maxLength={500}
                        disabled={qrCodeType !== "url"}
                        style={{
                            width: "400px",
                            opacity: qrCodeType !== "url" ? 0.5 : 1,
                        }}
                        className={classnames({
                            "not-allowed-input__clz": qrCodeType !== "url",
                        })}
                    />
                </div>

                <div className="d-flex align-items-start">
                    <input
                        type="radio"
                        id="qr-type-image"
                        name="qrCodeType"
                        checked={qrCodeType === "image"}
                        onChange={() => {
                            setQrCodeType("image");
                            setQrCodeUrl("");
                        }}
                        className="mr-2 mt-1"
                    />
                    <label htmlFor="qr-type-image" className="mb-0 mr-3">
                        {t("Print fixed QR")} ( image )
                    </label>
                </div>

                {qrCodeType === "image" && (
                    <div
                        className="d-flex flex-wrap justify-content-center align-items-center mt-3 ml-4"
                        style={{
                            border: dragging ? "dashed grey 4px" : "unset",
                            borderRadius: "6px",
                            height: "100%",
                            width: "auto",
                            padding: "1rem",
                        }}
                        onDragLeave={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragging(false);
                        }}
                        onDragOver={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragging(true);
                        }}
                        onDrop={handleDrop}
                    >
                        <Card
                            className="uploader__clz"
                            style={{
                                border: "2px dashed #ccc",
                                borderRadius: "8px",
                                width: "100%",
                                minHeight: "200px",
                            }}
                        >
                            {qrCodeImage ? (
                                <React.Fragment>
                                    <div
                                        className="d-flex justify-content-center align-items-center"
                                        style={{
                                            height: "70%",
                                            width: "100%",
                                            padding: "20px",
                                        }}
                                    >
                                        <img
                                            src={
                                                displayImage === ""
                                                    ? (qrCodeImage as string)
                                                    : displayImage
                                            }
                                            alt="QR Code"
                                            style={{
                                                maxWidth: "200px",
                                                maxHeight: "100px",
                                            }}
                                        />
                                    </div>
                                    <div className="d-flex justify-content-around pb-3">
                                        <label
                                            htmlFor="qr-code-file"
                                            style={{
                                                cursor: "pointer",
                                                color: "#007bff",
                                                textDecoration: "underline",
                                                marginRight: "10px",
                                            }}
                                        >
                                            {t("Browse files")}
                                        </label>
                                        <button
                                            type="button"
                                            aria-label={t(
                                                "Delete QR code image"
                                            )}
                                            onClick={handleDeleteImage}
                                            style={{
                                                background: "none",
                                                border: "none",
                                                padding: 0,
                                                cursor: "pointer",
                                            }}
                                        >
                                            <TrashIcon
                                                height={25}
                                                width={25}
                                                fill="red"
                                            />
                                        </button>
                                    </div>
                                </React.Fragment>
                            ) : (
                                <label
                                    htmlFor="qr-code-file"
                                    style={{
                                        cursor: "pointer",
                                        display: "flex",
                                        flexDirection: "column",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        padding: "40px",
                                        textAlign: "center",
                                    }}
                                >
                                    <MdCloudUpload
                                        style={{
                                            width: "80px",
                                            height: "80px",
                                            color: "rgba(81, 77, 77, 0.85)",
                                            marginBottom: "15px",
                                        }}
                                    />

                                    <span
                                        style={{
                                            font:
                                                "normal normal 900 14px/22px Lato",
                                            color: "#2B2828",
                                        }}
                                    >
                                        {t("Drag and drop your QR code here")}
                                    </span>
                                    <span
                                        style={{
                                            color: "#007bff",
                                            textDecoration: "underline",
                                            fontSize: "14px",
                                            marginTop: "5px",
                                        }}
                                    >
                                        {t("Browse files")}
                                    </span>
                                </label>
                            )}
                            <input
                                type="file"
                                id="qr-code-file"
                                accept="image/png"
                                onChange={handleFileChange}
                                style={{ display: "none" }}
                            />
                        </Card>
                    </div>
                )}
            </div>
        </div>
    );
}
